import {
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";
import { formatDate } from "@sp-crm/core";
import { WorkflowExecutionResult } from "generated/graphql";
import React from "react";

export interface WorkflowRunState {
    scheduledAt: string | null;
    cancelledAt: string | null;
    finishedAt: string | null;
    result: WorkflowExecutionResult | null;
    resultDetail: string | null;
}

export type WorkflowRunDisplayState =
    | "success"
    | "warning"
    | "error"
    | "cancelled"
    | "scheduled"
    | "queued";

export const runDisplayState = (run: WorkflowRunState): WorkflowRunDisplayState => {
    if (run.finishedAt) {
        if (run.result === WorkflowExecutionResult.Error) {
            return "error";
        }
        return run.result === WorkflowExecutionResult.Warning ? "warning" : "success";
    }
    if (run.cancelledAt) {
        return "cancelled";
    }
    return run.scheduledAt ? "scheduled" : "queued";
};

const activeTitleClassName = "text-sm font-medium text-gray-900";

export const mutedTitleClassName = "text-sm font-medium text-gray-400";

export const runStatusTextClassName = "text-xs text-gray-500";

export const runTitleClassName = (state: WorkflowRunDisplayState): string =>
    state === "cancelled" ? mutedTitleClassName : activeTitleClassName;

export const stopWorkflowConfirm = {
    title: "Remove",
    message: "Are you sure you want to stop this workflow from running?",
    confirmLabel: "Yes, stop this workflow",
    cancelLabel: "No, keep it running",
};

export const runStatusText = (run: WorkflowRunState): string | null => {
    const state = runDisplayState(run);
    switch (state) {
        case "success":
            return formatDate(run.finishedAt);
        case "warning":
            return `completed with warnings at ${formatDate(run.finishedAt)}`;
        case "error":
            return `completed with errors at ${formatDate(run.finishedAt)}`;
        case "cancelled":
            return `cancelled at ${formatDate(run.cancelledAt)}`;
        case "scheduled":
            return `scheduled to run at ${formatDate(run.scheduledAt)}`;
        case "queued":
            return null;
        default: {
            const exhaustiveCheck: never = state;
            throw new Error(`Unhandled run state: ${exhaustiveCheck}`);
        }
    }
};

interface RunStatusIconProps {
    state: WorkflowRunDisplayState;
}

export const RunStatusIcon: React.FC<RunStatusIconProps> = props => {
    const { state } = props;
    const sizing = "h-5 w-5 shrink-0";
    switch (state) {
        case "success":
            return <CheckCircleIcon className={`${sizing} text-green-600`} />;
        case "warning":
            return <ExclamationTriangleIcon className={`${sizing} text-yellow-500`} />;
        case "error":
            return <XCircleIcon className={`${sizing} text-red-500`} />;
        case "cancelled":
            return <XCircleIcon className={`${sizing} text-gray-300`} />;
        case "scheduled":
        case "queued":
            return <ClockIcon className={`${sizing} text-yellow-500`} />;
        default: {
            const exhaustiveCheck: never = state;
            throw new Error(`Unhandled run state: ${exhaustiveCheck}`);
        }
    }
};

interface ResultDetailProps {
    detail: string | null;
    className: string;
}

export const ResultDetail: React.FC<ResultDetailProps> = props => {
    const { detail, className } = props;
    if (!detail) {
        return null;
    }
    return (
        <div
            className={`text-sm text-gray-700 break-words whitespace-pre-line ${className}`}>
            {detail}
        </div>
    );
};
